import {Injectable, NgZone} from '@angular/core';
import {environment} from '../../environments/environment';

/**
 * Chiavi inbound accettate da `receiveValuesFromNative`.
 *
 * - `auth`: chiavi sensibili (token, auth_mode). NON vengono mai scritte in
 *   localStorage: sono instradate ai setter sessionStorage di LocalStorageService
 *   (vedi `applyInboundValues` in AppInitService). Qui il bridge le conserva solo
 *   nel payload risolto da `valuesReady`.
 * - `nonSensitive`: chiavi non sensibili note (es. platform). Persistite in localStorage.
 *
 * Qualsiasi chiave non presente in queste liste viene ignorata.
 */
export const INBOUND_AUTH_KEYS = ['access_token', 'refresh_token', 'auth_mode', 'spid_access_token'] as const;
export const INBOUND_NON_SENSITIVE_KEYS = ['platform'] as const;
export const ALLOWED_INBOUND_KEYS: readonly string[] = [...INBOUND_AUTH_KEYS, ...INBOUND_NON_SENSITIVE_KEYS];

/**
 * Chiavi auth che NON devono mai essere persistite nelle SharedPreferences native
 * (token in chiaro su disco). `saveSharedPreferences` è no-op per queste chiavi.
 */
const AUTH_KEYS_NO_PERSIST: readonly string[] = [
  'access_token',
  'refresh_token',
  'expiration',
  'auth_mode',
  'user',
  'spid_access_token',
  'spid_refresh_token',
  'spid_expiration',
];

@Injectable({
  providedIn: 'root'
})
export class JsBridgeService {
  private valuesReadyResolve!: (value: any) => void;
  valuesReady = new Promise((resolve) => {
    this.valuesReadyResolve = resolve;
  });
  private resolved = false;

  /**
   * Handler della singola operazione passkey in corso (una alla volta: la UI di sistema del
   * Credential Manager è modale). Risolta/rigettata dalle callback `onPasskey*` SOLO se il
   * `requestId` echeggiato dal nativo combacia con quello dell'operazione (correlazione
   * request↔response: scarta le callback stantie di operazioni precedenti/annullate).
   */
  private passkeyPending: {
    requestId: string;
    resolve: (responseJson: string) => void;
    reject: (err: PasskeyBridgeError) => void;
  } | null = null;

  /** Contatore monotono per generare i `requestId` di correlazione passkey (unico per sessione WebView). */
  private passkeySeq = 0;

  constructor(private readonly ngZone: NgZone) {
    // Callback passkey native→web: consegnano l'esito dell'operazione avviata via bridge.
    // Il primo argomento è il `requestId` echeggiato dal nativo (correlazione request↔response).
    (window as any).onPasskeyRegistrationResult = (requestId: string, responseJson: string) => this.settlePasskey(requestId, responseJson);
    (window as any).onPasskeyAuthenticationResult = (requestId: string, responseJson: string) => this.settlePasskey(requestId, responseJson);
    (window as any).onPasskeyError = (requestId: string, code: string, message: string) => this.rejectPasskey(requestId, code, message);

    (window as any).receiveValuesFromNative = (data: string) => {
      try {
        const keyValueObject = JSON.parse(data ?? 'null');

        if (keyValueObject && typeof keyValueObject === 'object') {
          // Mai loggare valori (contengono i token). Solo i nomi delle chiavi, e solo in dev.
          if (!environment.production) {
            console.debug('[JSBridge] receiveValuesFromNative keys:', Object.keys(keyValueObject));
          }

          // Allow-list: le chiavi non sensibili note vanno in localStorage; le chiavi
          // sensibili (token/auth_mode) NON vengono scritte qui — le instrada il
          // consumatore di `valuesReady` verso sessionStorage. Tutto il resto è ignorato.
          for (const key of INBOUND_NON_SENSITIVE_KEYS) {
            const value = keyValueObject[key];
            if (value === undefined || value === null) {
              continue;
            }
            try {
              localStorage.setItem(key, String(value));
            } catch (e) {
              console.warn('[JSBridge] localStorage set failed', {key, e});
            }
          }
        }

        this.safeResolve(keyValueObject);
      } catch (e) {
        console.error('[JSBridge] receiveValuesFromNative failed, resolving anyway', e);
        this.safeResolve(undefined);
      }
    };
  }

  /*
  MARK: FUNZIONI DI SCRITTURA SU PONTE JS
   */
  openPdf(url: string) {
    var isWebApp = false;
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.openPDF.postMessage(url)
        return;
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }

    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.openPDF(url)
        return;
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }

    window.open(url, '_blank')
  }

  openExternalUrl(url: string): void {
    if (!url) {
      console.warn('[JSBridge] openExternalUrl: invalid url', url);
      return;
    }

    const trimmedUrl = url.trim();
    console.log('[JSBridge] openExternalUrl called:', trimmedUrl);

    // iOS
    try {
      if ((window as any)?.webkit?.messageHandlers?.openExternalUrl) {
        (window as any).webkit.messageHandlers.openExternalUrl.postMessage(trimmedUrl);
        return;
      }
    } catch (err) {
      console.log('[JSBridge] iOS native context does not exist yet');
    }

    // Android
    try {
      if ((window as any)?.JSInterface?.openExternalUrl) {
        (window as any).JSInterface.openExternalUrl(trimmedUrl);
        return;
      }
    } catch (err) {
      console.log('[JSBridge] Android native context does not exist yet');
    }

    // Fallback Web
    console.log('[JSBridge] Web fallback: window.open');
    window.open(trimmedUrl, '_blank', 'noopener');
  }

  login(userLogin: UserLogin) {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.login.postMessage(userLogin)
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }

    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.login(JSON.stringify(userLogin))
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }
  }

  logout(userLogout: UserLogin) {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.logout.postMessage(userLogout)
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }

    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.logout(JSON.stringify(userLogout))
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }
  }

  appInitEnd() {
    console.log('[JSBridge] appInitEnd called');
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        console.log('[JSBridge] calling ios appInitEnd');

        (window as any).webkit.messageHandlers.appInitEnd.postMessage(true)
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }

    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        console.log('[JSBridge] calling Android appInitEnd');

        (window as any).JSInterface.appInitEnd(true)
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }
  }

  startScanner() {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.startScanner.postMessage(true)
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }

    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.startScanner(true)
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }
  }


  clearPushNotification() {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.clearPushNotification.postMessage(true)
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }

    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.clearPushNotification(true)
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }
  }

  openFile(blob: any, mimeType: string, fileName: string) {
    console.log('[JSBridge] openFile called', {
      hasJSInterface: !!(window as any).JSInterface,
      mimeType,
      fileName,
      payloadType: typeof blob,
      payloadLength: typeof blob === 'string' ? blob.length : undefined
    });

    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.downloadFile.postMessage({
          blob: blob, mimeType: mimeType, fileName: fileName
        });
        return;
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }
    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.downloadFile(blob, mimeType, fileName)
        return;
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }

  }

  setModePortrait() {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.setPortrait.postMessage(true);
        return;
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }
    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.setPortrait()
        return;
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }

  }

  setModeLandscape() {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.setLandscape.postMessage(true);
        return;
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }
    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.setLandscape()
        return;
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }

  }

  uploadFile() {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.uploadFile.postMessage(true);
        return;
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }
    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.uploadFile()
        return;
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }

  }

  isNativeContext(): boolean {
    const w = window as any;
    // Check handler specifici dell'app nativa (non il generico webkit.messageHandlers
    // che potrebbe essere esposto da browser iOS basati su WKWebView come Chrome)
    const hasIOSBridge = !!w?.webkit?.messageHandlers?.login;
    const hasAndroidBridge = !!w?.JSInterface;
    const result = hasIOSBridge || hasAndroidBridge;
    console.log('[JSBridge] isNativeContext:', result);
    return result;
  }

  // MARK: Passkey native (Credential Manager)

  /**
   * @returns true se il guscio nativo espone il ponte passkey ed è supportato dal device.
   *
   * **Asincrona per necessità, non per simmetria**: il ponte Android risponde in modo sincrono,
   * quello iOS è un handler *con reply* (`WKScriptMessageHandlerWithReply`) e restituisce una
   * `Promise`. Il valore di ritorno è quindi una Promise per entrambi.
   *
   * Su iOS l'handler DEVE essere registrato con reply: se fosse un handler normale, `postMessage`
   * ritornerebbe `undefined`, `await undefined` è `undefined` e `!!undefined` è `false` — nessun
   * errore, nessun log, e il percorso passkey semplicemente non verrebbe mai offerto.
   */
  async isPasskeySupported(): Promise<boolean> {
    const w = window as any;

    // Android: valore diretto, nessuna Promise nel ponte nativo.
    try {
      if (typeof w?.JSInterface?.isPasskeySupported === 'function') {
        return !!w.JSInterface.isPasskeySupported();
      }
    } catch (err) {
      console.warn('[JSBridge] isPasskeySupported (Android) failed', err);
    }

    // iOS: handler con reply, esito asincrono.
    try {
      if (typeof w?.webkit?.messageHandlers?.isPasskeySupported?.postMessage === 'function') {
        return !!(await w.webkit.messageHandlers.isPasskeySupported.postMessage(true));
      }
    } catch (err) {
      console.warn('[JSBridge] isPasskeySupported (iOS) failed', err);
    }

    return false;
  }

  /**
   * Avvia la registrazione passkey via Credential Manager nativo.
   * @param requestJson PublicKeyCredentialCreationOptions in formato JSON WebAuthn.
   * @returns registrationResponseJson prodotto dal Credential Manager.
   */
  startPasskeyRegistration(requestJson: string): Promise<string> {
    return this.startPasskeyOperation('startPasskeyRegistration', requestJson);
  }

  /**
   * Avvia l'autenticazione passkey via Credential Manager nativo.
   * @param requestJson PublicKeyCredentialRequestOptions in formato JSON WebAuthn.
   * @returns authenticationResponseJson prodotto dal Credential Manager.
   */
  startPasskeyAuthentication(requestJson: string): Promise<string> {
    return this.startPasskeyOperation('startPasskeyAuthentication', requestJson);
  }

  private startPasskeyOperation(method: 'startPasskeyRegistration' | 'startPasskeyAuthentication', requestJson: string): Promise<string> {
    const w = window as any;
    if (this.passkeyPending) {
      return Promise.reject(new PasskeyBridgeError('IN_PROGRESS', 'Operazione passkey già in corso'));
    }
    const hasAndroid = typeof w?.JSInterface?.[method] === 'function';
    const hasIOS = typeof w?.webkit?.messageHandlers?.[method]?.postMessage === 'function';
    if (!hasAndroid && !hasIOS) {
      return Promise.reject(new PasskeyBridgeError('UNSUPPORTED', 'Ponte passkey nativo non disponibile'));
    }
    const requestId = `pk-${++this.passkeySeq}`;
    return new Promise<string>((resolve, reject) => {
      this.passkeyPending = {requestId, resolve, reject};
      try {
        if (hasAndroid) {
          // Android: due parametri posizionali, il `requestId` per primo. Il nativo lo echeggia
          // nelle callback di esito.
          w.JSInterface[method](requestId, requestJson);
        } else {
          // iOS: UN SOLO argomento, un oggetto. `postMessage` accetta un unico parametro, quindi
          // passarne due farebbe perdere `requestJson` IN SILENZIO (limite dell'API WebKit, non
          // un difetto del guscio). Le callback di esito restano identiche ad Android.
          w.webkit.messageHandlers[method].postMessage({requestId, requestJson});
        }
      } catch (err: any) {
        this.passkeyPending = null;
        reject(new PasskeyBridgeError('UNKNOWN', err?.message || 'Invocazione bridge passkey fallita'));
      }
    });
  }

  private settlePasskey(requestId: string, responseJson: string): void {
    const pending = this.passkeyPending;
    // Correlazione: settla solo se l'esito appartiene all'operazione corrente. Una callback
    // stantia (op precedente/annullata) ha un `requestId` diverso e va ignorata, lasciando
    // l'operazione corrente in attesa della propria callback.
    if (!pending || pending.requestId !== requestId) {
      console.warn('[JSBridge] callback passkey stantia ignorata (requestId non corrispondente)', requestId);
      return;
    }
    this.passkeyPending = null;
    // La callback nativa arriva fuori dalla zona Angular: rientriamo così che il change
    // detection dopo il .then del chiamante (login/profilo) funzioni.
    this.runInZone(() => pending.resolve(responseJson));
  }

  private rejectPasskey(requestId: string, code: string, message: string): void {
    const pending = this.passkeyPending;
    if (!pending || pending.requestId !== requestId) {
      console.warn('[JSBridge] errore passkey stantio ignorato (requestId non corrispondente)', requestId);
      return;
    }
    this.passkeyPending = null;
    this.runInZone(() => pending.reject(new PasskeyBridgeError(code, message)));
  }

  private runInZone(fn: () => void): void {
    // La zona arriva via DI (NgZone), non più da `window.AppComponentRef`: il servizio è
    // `providedIn:'root'` e non dipende dal ciclo di vita di AppComponent per settlare le
    // operazioni passkey (che possono arrivare fuori dalla zona Angular via evaluateJavascript).
    this.ngZone.run(fn);
  }


  saveSharedPreferences(key: string, value: string) {
    // No-op per le chiavi auth: i token non devono finire nelle SharedPreferences
    // native in chiaro (vedi capability volatile-session).
    if (AUTH_KEYS_NO_PERSIST.includes(key)) {
      return;
    }
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.saveInSharedPreferences.postMessage({
          key: key, value: value
        });
        return;
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }
    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.saveInSharedPreferences(key, value)
        return;
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }

  }

  getSharedValues(keys: string[]) {
    const w = window as any;

    try {
      if (w?.webkit?.messageHandlers?.getValues) {
        w.webkit.messageHandlers.getValues.postMessage(JSON.stringify(keys));
        return;
      }
    } catch {
    }

    try {
      if (w?.JSInterface?.getValues) {
        w.JSInterface.getValues(JSON.stringify(keys));
        return;
      }
    } catch {
    }

    console.log('[JSBridge] Web context -> resolve immediately');
    this.safeResolve(undefined);
  }

  private safeResolve(value: any) {
    if (this.resolved) return;
    this.resolved = true;
    this.valuesReadyResolve(value);
  }

  removeSharedValues(keys: string[]) {
    try {
      if ((window as any).webkit !== undefined && (window as any).webkit.messageHandlers !== undefined) {
        (window as any).webkit.messageHandlers.removeValues.postMessage(JSON.stringify(keys));
        return;
      }
    } catch (err) {
      console.log('iOS native context does not exist yet')
    }
    try {
      if ((window as any).JSInterface !== undefined && (window as any).JSInterface !== null) {
        (window as any).JSInterface.removeValues(JSON.stringify(keys))
        return;
      }
    } catch (err) {
      console.log('Android context does not exist yet')
    }

  }
}

export interface UserLogin {
  userId: string;
  accessToken: string;
}

/**
 * Errore normalizzato del ponte passkey nativo. Il `code` è il codice del Credential Manager
 * mappato lato nativo (USER_CANCELLED/NO_CREDENTIAL/UNSUPPORTED/INTERRUPTED/UNKNOWN) o un codice
 * lato bridge (IN_PROGRESS/UNSUPPORTED/UNKNOWN).
 */
export class PasskeyBridgeError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'PasskeyBridgeError';
  }
}
